import { useQuery } from "@tanstack/react-query";
import type {
  RankingPeriod,
  RankingScope,
} from "../repositories/gamificationRepository";
import { gamificationRepository } from "../repositories/gamificationRepository";

// Query keys
export const gamificationPointsQueryKey = (
  terreiroId: string,
  mediumId: string,
) => ["gamification", "points", terreiroId, mediumId];

export const gamificationHistoryQueryKey = (mediumId: string) => [
  "gamification",
  "history",
  mediumId,
];

export const gamificationRankingsQueryKey = (
  scope: RankingScope,
  scopeId: string | null,
  period: RankingPeriod,
) => ["gamification", "rankings", scope, scopeId, period];

export const gamificationBadgesQueryKey = (mediumId: string) => [
  "gamification",
  "badges",
  mediumId,
];

export const gamificationBadgeProgressQueryKey = (
  terreiroId: string,
  mediumId: string,
) => ["gamification", "badges", terreiroId, mediumId, "progress"];

export const gamificationSummaryQueryKey = (
  terreiroId: string,
  mediumId: string,
) => ["gamification", "summary", terreiroId, mediumId];

// Query hooks
interface UseGamificationPointsQueryProps {
  terreiroId: string;
  mediumId: string;
  enabled?: boolean;
}

export const useGamificationPointsQuery = ({
  terreiroId,
  mediumId,
  enabled = true,
}: UseGamificationPointsQueryProps) => {
  return useQuery({
    queryKey: gamificationPointsQueryKey(terreiroId, mediumId),
    queryFn: () => gamificationRepository.getPoints(terreiroId, mediumId),
    enabled: enabled && !!mediumId && !!terreiroId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
    retry: 2,
    refetchOnMount: "always",
  });
};

interface UseGamificationHistoryQueryProps {
  terreiroId: string;
  mediumId: string;
  limit?: number;
  enabled?: boolean;
}

export const useGamificationHistoryQuery = ({
  terreiroId,
  mediumId,
  limit,
  enabled = true,
}: UseGamificationHistoryQueryProps) => {
  return useQuery({
    queryKey: [...gamificationHistoryQueryKey(mediumId), limit],
    queryFn: () =>
      gamificationRepository.getPointHistory(terreiroId, mediumId, limit),
    enabled: enabled && !!mediumId && !!terreiroId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    retry: 2,
    refetchOnMount: "always",
  });
};

interface UseGamificationRankingsQueryProps {
  scope: RankingScope;
  scopeId: string | null;
  period: RankingPeriod;
  mediumId?: string;
  enabled?: boolean;
}

export const useGamificationRankingsQuery = ({
  scope,
  scopeId,
  period,
  mediumId,
  enabled = true,
}: UseGamificationRankingsQueryProps) => {
  return useQuery({
    queryKey: gamificationRankingsQueryKey(scope, scopeId, period),
    queryFn: () =>
      gamificationRepository.getRankings(scope, scopeId, period, mediumId),
    enabled: enabled && (scope !== "TERREIRO" || !!scopeId),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
    retry: 2,
    refetchOnMount: "always",
  });
};

interface UseTerreiroRankingsQueryProps {
  terreiroId: string;
  period?: RankingPeriod;
  mediumId?: string;
  enabled?: boolean;
}

export const useTerreiroRankingsQuery = ({
  terreiroId,
  period = "MONTHLY",
  mediumId,
  enabled = true,
}: UseTerreiroRankingsQueryProps) => {
  return useQuery({
    queryKey: gamificationRankingsQueryKey("TERREIRO", terreiroId, period),
    queryFn: () =>
      gamificationRepository.getTerreiroRankings(terreiroId, period, mediumId),
    enabled: enabled && !!terreiroId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
    retry: 2,
    refetchOnMount: "always",
  });
};

interface UseGamificationBadgesQueryProps {
  mediumId: string;
  enabled?: boolean;
}

export const useGamificationBadgesQuery = ({
  mediumId,
  enabled = true,
}: UseGamificationBadgesQueryProps) => {
  return useQuery({
    queryKey: gamificationBadgesQueryKey(mediumId),
    queryFn: () => gamificationRepository.getMediumBadges(mediumId),
    enabled: enabled && !!mediumId,
    staleTime: 1000 * 60 * 10, // 10 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
    retry: 2,
  });
};

interface UseGamificationBadgeProgressQueryProps {
  terreiroId: string;
  mediumId: string;
  enabled?: boolean;
}

export const useGamificationBadgeProgressQuery = ({
  terreiroId,
  mediumId,
  enabled = true,
}: UseGamificationBadgeProgressQueryProps) => {
  return useQuery({
    queryKey: gamificationBadgeProgressQueryKey(terreiroId, mediumId),
    queryFn: () =>
      gamificationRepository.getBadgeProgress(terreiroId, mediumId),
    enabled: enabled && !!mediumId && !!terreiroId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
    retry: 2,
    refetchOnMount: "always",
  });
};

interface UseGamificationSummaryQueryProps {
  terreiroId: string;
  mediumId: string;
  enabled?: boolean;
}

export const useGamificationSummaryQuery = ({
  terreiroId,
  mediumId,
  enabled = true,
}: UseGamificationSummaryQueryProps) => {
  return useQuery({
    queryKey: gamificationSummaryQueryKey(terreiroId, mediumId),
    queryFn: () => gamificationRepository.getSummary(terreiroId, mediumId),
    enabled: enabled && !!mediumId && !!terreiroId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
    retry: 2,
    refetchOnMount: "always",
  });
};
